/* eslint-disable @typescript-eslint/no-explicit-any */
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';

import { Toast } from 'sharedUi/Toast';
import PageSkeleton from '../../components/common/page-skeleton';
import ScreenHeader from '../../components/common/screen-header';
import { Button } from 'sharedUi/Button';
import { TextArea } from 'sharedUi/TextArea';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import FormSelect from '../../components/forms/form-select';
import { useAccessibleProjects, projectFieldOptions, appIdFromField, defaultProjectId, referencableIn, SHARED_PROJECT } from '../../hooks/useProjectScope';
import ChannelTemplateEditor from '../../components/forms/channel-template-editor';
import ConditionTreeEditor, { type ConditionTree } from '../../components/forms/condition-tree-editor';
import ParameterEditor from '../../components/forms/parameter-editor';
import RecipientEditor from '../../components/forms/recipient-editor';
import {
  notificationMastersService, type Recipient, type ParameterDefinition,
} from '../../services/notification-masters.service';
import { notificationTypesService } from '../../services/notification-types.service';
import { recipientMastersService } from '../../services/recipient-masters.service';
import { extractErrorMessage } from '../../utils/error-handler';

const schema = z.object({
  // Empty means shared; the API honours this only inside the caller's scope.
  // Required: a blank appId is what the API reads as "shared with every
  // project", so an unset field would quietly author a global row.
  appId: z.string().min(1, 'Choose the project this belongs to.'),
  notificationTypeId: z.string().min(1, 'A notification inherits its code and delivery policy from a type'),
  description: z.string().max(1000).optional().or(z.literal('')),
  channelTemplates: z.record(z.string(), z.any()).optional(),
  parameters: z.array(z.any()).optional(),
  conditionTree: z.any().optional().nullable(),
  recipients: z.array(z.any()).optional(),
});
type FormData = z.infer<typeof schema>;


const NotificationMasterForm = () => {
  const { id, isReady, navigateToList } = useSecureNavigation({ entity: 'notification', basePath: '/notification-masters' });
  const isEdit = Boolean(id);
  const { projects, canScopeByProject } = useAccessibleProjects();
  const queryClient = useQueryClient();
  const toast = useRef<Toast>(null);

  const { control, handleSubmit, reset, watch, formState: { errors } } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      appId: defaultProjectId(projects),
      notificationTypeId: '', description: '',
      channelTemplates: {}, parameters: [], conditionTree: null, recipients: [],
    },
    mode: 'onChange',
  });
  // A notification delivers on its type's channels and has no list of its
  // own, so the template tabs follow the type picked above.
  // A condition may only test what the trigger supplies, so the field
  // picker offers exactly the parameters this notification declares.
  const declaredParameters = ((watch('parameters') || []) as ParameterDefinition[])
    .map((p) => p.name)
    .filter(Boolean);


  const { data: typesPage } = useQuery({
    queryKey: ['notificationTypes', 'lookup'],
    queryFn: () => notificationTypesService.list({ skip: 0, limit: 200 }),
    staleTime: 3e5,
  });
  const { data: recipientsPage } = useQuery({
    queryKey: ['recipientMasters', 'lookup'],
    queryFn: () => recipientMastersService.list({ skip: 0, limit: 200 }),
    staleTime: 3e5,
  });

  /* Only what this notification may actually reference: its own project's rows
     and the shared ones. The lists come back scoped to the VIEWER, so without
     this an administrator who can see several projects is offered another
     project's type or recipient and finds out at save, where the API answers
     "does not exist in this scope" — a true statement that reads as a bug. */
  const projectId = appIdFromField(watch('appId'));

  const typeOptions = (typesPage?.data || [])
    .filter((t) => t.isActive !== false)
    .filter(referencableIn(projectId))
    .map((t) => ({ value: t._id, label: t.notificationCode }));
  // Deactivating stops new use outright — the API refuses such a reference, so
  // it is not offered here either.
  const recipientOptions = (recipientsPage?.data || [])
    .filter((r) => r.isActive !== false)
    .filter(referencableIn(projectId))
    .map((r) => ({ value: r._id, label: r.recipientCode }));

  // The type owns delivery policy, so its channel list is the sensible default
  // for a new notification — the author narrows or extends from there.
  const selectedType = (typesPage?.data || []).find((t) => t._id === watch('notificationTypeId'));
  const typeChannels: string[] = selectedType?.channels || [];

  const { data: definition, isLoading } = useQuery({
    queryKey: ['notificationMaster', id],
    queryFn: () => notificationMastersService.getById(id!),
    enabled: isEdit && isReady && !!id,
  });

  useEffect(() => {
    if (definition && isEdit) {
      reset({
        appId: definition.appId || SHARED_PROJECT,
        notificationTypeId: String(definition.notificationTypeId || ''),
        description: definition.description || '',
        channelTemplates: (definition.channelTemplates as any) || {},
        parameters: definition.parameters || [],
        conditionTree: (definition.conditionTree as any) || null,
        recipients: definition.recipients || [],
      });
    }
  }, [definition, isEdit, reset]);

  const mutation = useMutation({
    mutationFn: (data: any) => (isEdit && id ? notificationMastersService.update(id, data) : notificationMastersService.create(data)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notificationMasters'] });
      toast.current?.show({ severity: 'success', summary: 'Success', detail: `Notification ${isEdit ? 'updated' : 'created'}`, life: 3000 });
      setTimeout(() => navigateToList(), 800);
    },
    onError: (e: any) => toast.current?.show({ severity: 'error', summary: 'Error', detail: extractErrorMessage(e, 'Save failed'), life: 10000 }),
  });

  const onSubmit = (d: FormData) => mutation.mutate({
    // Create only: the update schema has no appId, so sending one on edit
    // would look like a move that silently did not happen.
    ...(isEdit ? {} : { appId: appIdFromField(d.appId) }),
    notificationTypeId: d.notificationTypeId,
    description: d.description || null,
    channelTemplates: d.channelTemplates || {},
    parameters: (d.parameters || []) as ParameterDefinition[],
    // null rather than {} — the dispatcher reads a falsy tree as "always fire",
    // and an empty object would be a tree with no match and no conditions.
    conditionTree: d.conditionTree || null,
    recipients: (d.recipients || []) as Recipient[],
  });

  if (isEdit && isLoading) return <PageSkeleton />;

  return (
    <div className="p-4" data-testid="SCR-NotificationMaster-Form">
      <Toast ref={toast} position="top-right" />
      <ScreenHeader icon="pi pi-bell" title={isEdit ? 'Edit Notification Master' : 'Create New Notification Master'} onBack={() => navigateToList()} />

      

      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4 max-w-5xl w-full">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {canScopeByProject && (
            <Controller name="appId" control={control} render={({ field }) => (
              <FormSelect label="Project" name={field.name} value={field.value || ''}
                options={projectFieldOptions(projects, field.value)}
                onChange={field.onChange} disabled={isEdit}
                helperText={isEdit
                  ? 'Set when this was created; the API does not move one afterwards.'
                  : 'Which project owns this. It cannot be moved afterwards.'} />
            )} />
          )}
          <Controller name="notificationTypeId" control={control} render={({ field }) => (
            <FormSelect label="Type" required name={field.name} value={field.value || ''} options={typeOptions} onChange={field.onChange}
              error={!!errors.notificationTypeId}
              helperText={errors.notificationTypeId?.message || 'The notification takes its code and delivery policy from here.'} />
          )} />
          <Controller name="description" control={control} render={({ field }) => (
            <div className="sm:col-span-2"><TextArea label="Description" {...field} rows={2} /></div>
          )} />
        </div>

        <div className="ds-card p-5">
          <Controller name="recipients" control={control} render={({ field }) => (
            <RecipientEditor value={(field.value || []) as Recipient[]} onChange={field.onChange}
              recipientOptions={recipientOptions} testId="NotificationMasterForm.Recipients" />
          )} />
        </div>

        <div className="ds-card p-5">
          <Controller name="parameters" control={control} render={({ field }) => (
            <ParameterEditor value={(field.value || []) as ParameterDefinition[]} onChange={field.onChange}
              testId="NotificationMasterForm.Parameters" />
          )} />
        </div>

        <div className="ds-card p-5">
          <Controller name="channelTemplates" control={control} render={({ field }) => (
            <ChannelTemplateEditor channels={typeChannels} value={field.value || {}}
              parameterNames={declaredParameters} appId={projectId}
              onChange={field.onChange} testId="NotificationMasterForm.Templates" />
          )} />
        </div>

        <div className="ds-card p-5">
          <Controller name="conditionTree" control={control} render={({ field }) => (
            <ConditionTreeEditor
              value={(field.value as ConditionTree) || null}
              onChange={field.onChange}
              parameterNames={declaredParameters}
              testId="NotificationMasterForm.Condition"
            />
          )} />
          <small className="text-xs text-gray-400">
            Whether this fires for a given payload — not <em>when</em>. The trigger source belongs to the
            schedule, so &quot;fire on event X&quot; is stated once.
          </small>
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" label="Cancel" severity="secondary" outlined onClick={() => navigateToList()} />
          <Button type="submit" label={mutation.isPending ? 'Saving…' : isEdit ? 'Update' : 'Create'} icon={mutation.isPending ? 'pi pi-spin pi-spinner' : 'pi pi-check'} disabled={mutation.isPending} />
        </div>
      </form>
    </div>
  );
};

export default NotificationMasterForm;
